/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */

import { from } from 'rxjs';
import { map } from 'rxjs/operators';

import constants, { publicConstants } from '../../constants';
import type { Action } from '../../store/state/state-manager';
import { fetch } from '../../service_broker/fetch';
import { resolveRestApi } from '../../router/app-router';
import { logAndCatchError } from '../../utils/rxjs-utils';
import {
  addActionsToParentContext,
  getActionPayload,
  processHeaders,
  processViewResponse,
  SCOPE_TYPES
} from '../../store/state/epics/util';
import { ACTION_GETOBJECTWIDEACTION } from '../../store/state/actions';
import { getResolvedTargetName } from '../../router/routing-manager';
import { buildContextName, buildModalContext } from '../../utils/context-utils';
import { getTranslatedDXAPIResponse } from '../../utils/dxapi-response-translator';
import {
  addContainerItem,
  updateContainerItem,
  postProcess,
  resetLoading,
  mergeData,
  activateContainerItem
} from '../../store/state/actions/action-creators';
import {
  getDataContextName,
  hasContainerItems,
  isContainerMultipleMode,
  getContainerItemName,
  isContainerItemExists,
  getChildContainers,
  getActiveContainerItemName
} from '../../container/container-utils';
import { isModalContainer, getContainerName } from '../../case/common-helper';
import ObjectTransformer from '../../utils/object-transformer';
import { isParentAndChildContextHoldingSameItem, updateKeyAndFlowName } from '../common-helper';
import type { Headers } from '../../case/types';

const populateContextAndContainerItem = (
  target: string,
  containerName: string,
  actionInContext: string,
  objectInfoID: string
) => {
  // build new context, containerItemID if the target doesn't have any containers
  // or always new for multiple mode target
  // or always new context, containerItemID for modal container
  let containerItemID;
  let context;
  let isNewContainerItem = false;
  const isMultiMode = isContainerMultipleMode(target);
  if (!hasContainerItems(target) || (isMultiMode && !isContainerItemExists(target, objectInfoID))) {
    context = buildContextName(containerName, actionInContext);
    if (isModalContainer(containerName)) {
      context = buildModalContext(actionInContext, context);
    }
    containerItemID = context;
    isNewContainerItem = true;
  } else {
    containerItemID = isMultiMode ? getContainerItemName(target, objectInfoID) : getActiveContainerItemName(target);
    context = getDataContextName(containerItemID);
  }
  return { context, containerItemID, isNewContainerItem };
};

const getCaseViewMode = (rootViewInfo: object) => {
  return rootViewInfo ? constants.PERFORM_MODE : constants.REVIEW_MODE;
};

const addActionCreator = (isNewContainerItem: boolean) => {
  return isNewContainerItem ? addContainerItem : updateContainerItem;
};

export const executeOpenObjectActionRequest = (actionInPayload: Action['payload']) => {
  const { actionID, caseID, context, openActionType, modalLoadingContainer } = actionInPayload;
  const viewType = SCOPE_TYPES.FORM;
  const restApiDetails = resolveRestApi(ACTION_GETOBJECTWIDEACTION, { objectID: caseID, actionID, viewType });
  const { reqType } = restApiDetails;
  let { restApi } = restApiDetails;
  actionInPayload.actionType = constants.RESOURCE_TYPES.OBJECT;

  if (restApi && !restApi.includes('viewType=')) {
    restApi = !restApi.includes('?') ? `${restApi}?viewType=${viewType}` : `${restApi}&viewType=${viewType}`;
  }
  const contextData = modalLoadingContainer?.context ?? context;
  return from(
    fetch(
      restApi,
      {
        method: reqType,
        headers: {}
      },
      contextData
    )
  ).pipe(
    map((response) => {
      ObjectTransformer.analyseResponse(response.data);
      return {
        response: response.data,
        actionInPayload,
        headers: processHeaders(response.headers)
      };
    }),
    logAndCatchError(`: Error occurred during ajax call at ${openActionType} Epic : `, contextData)
  );
};

export const postProcessOpenObjectActionRequest = async (
  response: any,
  actionsOut: Action[],
  { headers, actionInPayload }: { headers: Headers; actionInPayload: Action['payload'] }
) => {
  const {
    context: actionInContext,
    actionName,
    openActionType,
    doDeleteCaseSummary,
    modalLoadingContainer
  } = actionInPayload;
  const translatedDXAPIResponse = getTranslatedDXAPIResponse(response, {
    info: publicConstants.OBJECT_INFO.OBJECT_INFO
  });
  const containerName = getContainerName(openActionType, actionInPayload);
  const target = modalLoadingContainer?.target ?? getResolvedTargetName(containerName, actionInContext);

  const { context, containerItemID, isNewContainerItem } = populateContextAndContainerItem(
    target,
    containerName,
    actionInContext,
    response.data.objectInfo.ID
  );

  /* [TODO]: we shouldn't use "acName" anywhere, setting this value..since app-router uses this logic */
  const options = { acName: target, target, containerName, context };
  await processViewResponse(translatedDXAPIResponse, actionInPayload, options);
  const rootViewInfo = translatedDXAPIResponse.uiResources?.root;
  const caseViewMode = getCaseViewMode(rootViewInfo);
  const payload = getActionPayload(translatedDXAPIResponse, {
    target,
    context,
    containerItemID,
    caseViewMode,
    doDeleteCaseSummary,
    headers,
    actionName,
    isMinimizable: false,
    isInCreateStage: isModalContainer(containerName), // to suppress the local actions buttons coming in Modal > Assignment
    resourceType: publicConstants.RESOURCE_TYPES.OBJECT,
    isProcessAction: actionInPayload.isProcessAction,
    showProgress: actionInPayload.showProgress,
    progressMessage: actionInPayload.progressMessage,
    actionType: actionInPayload.actionType
  });

  // updating key and flowName
  if (rootViewInfo) {
    updateKeyAndFlowName(payload, translatedDXAPIResponse);
  }

  // deleting doDeleteCaseSummary as it is not required during modal container case
  if (isModalContainer(containerName)) {
    delete payload.doDeleteCaseSummary;
  }

  actionsOut.push(postProcess(addActionCreator(isNewContainerItem), payload));

  if (!isNewContainerItem) {
    actionsOut.push(postProcess(resetLoading, payload));
  }
  /**
   * Use case : Launch local action and perform cancel
   * To merge latest response headers(eg: eTag) to respective workarea.
   */
  if (isNewContainerItem) {
    const containersList = getChildContainers(actionInPayload.contextName);
    // @ts-expect-error
    const activeContainerName = getContainerItemName(containersList, response.data.objectInfo.ID);
    if (activeContainerName) {
      const parentPayload = {
        data: {
          objectInfo: {
            headers
          }
        },
        context: activeContainerName
      };
      actionsOut.push(postProcess(mergeData, parentPayload));
    }
  }

  if (containerItemID !== getActiveContainerItemName(target)) {
    actionsOut.push(postProcess(activateContainerItem, { target, containerItemID }));
  }

  if (isParentAndChildContextHoldingSameItem(containerItemID) && !modalLoadingContainer?.context) {
    addActionsToParentContext(payload, actionsOut);
  }

  return actionsOut;
};
